<script>
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import FormButton from 'v3/components/Form/Button.vue';
import authAPI from 'dashboard/api/auth';

export default {
  components: { FormButton },
  props: {
    otpRequiredForLogin: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      // 'idle' | 'setup'
      step: 'idle',
      qrSvg: '',
      otpSecret: '',
      otpCode: '',
      isLoading: false,
      inputStyles: {
        borderRadius: '12px',
        padding: '6px 12px',
        fontSize: '14px',
        marginBottom: '2px',
      },
    };
  },
  computed: {
    isEnabled() {
      return this.otpRequiredForLogin;
    },
  },
  methods: {
    async startSetup() {
      this.isLoading = true;
      try {
        const { data } = await authAPI.getTwoFactorQrCode();
        this.qrSvg = data.qr_svg;
        this.otpSecret = data.otp_secret;
        this.otpCode = '';
        this.step = 'setup';
      } catch (error) {
        useAlert(parseAPIErrorResponse(error) || this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.FETCH_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    async confirmEnable() {
      if (!this.otpCode || this.otpCode.length < 6) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.OTP_REQUIRED'));
        return;
      }
      this.isLoading = true;
      try {
        await authAPI.enableTwoFactor({ otpAttempt: this.otpCode });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.ENABLE_SUCCESS'));
        this.step = 'idle';
        this.otpCode = '';
        this.$emit('update', true);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error) || this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.ENABLE_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    async disableTwoFactor() {
      this.isLoading = true;
      try {
        await authAPI.disableTwoFactor({});
        useAlert(this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.DISABLE_SUCCESS'));
        this.$emit('update', false);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error) || this.$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.DISABLE_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    cancel() {
      this.step = 'idle';
      this.otpCode = '';
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- 状态展示 + 操作按钮 -->
    <div v-if="step === 'idle'">
      <div class="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
            :class="isEnabled ? 'bg-green-100 dark:bg-green-900' : 'bg-slate-200 dark:bg-slate-600'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" :class="isEnabled ? 'text-green-600' : 'text-slate-500'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
              {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.STATUS_LABEL') }}
            </p>
            <p class="text-xs mt-0.5" :class="isEnabled ? 'text-green-600 dark:text-green-400' : 'text-slate-400'">
              {{ isEnabled ? $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.STATUS_ENABLED') : $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.STATUS_DISABLED') }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <FormButton
            v-if="!isEnabled"
            color-scheme="primary"
            variant="solid"
            size="medium"
            :disabled="isLoading"
            @click="startSetup"
          >
            {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.ENABLE_BTN') }}
          </FormButton>
          <template v-else>
            <FormButton
              color-scheme="secondary"
              variant="outline"
              size="medium"
              :disabled="isLoading"
              @click="startSetup"
            >
              {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.RESET_BTN') }}
            </FormButton>
            <FormButton
              color-scheme="danger"
              variant="solid"
              size="medium"
              :disabled="isLoading"
              @click="disableTwoFactor"
            >
              {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.DISABLE_BTN') }}
            </FormButton>
          </template>
        </div>
      </div>
    </div>

    <!-- 扫码绑定步骤 -->
    <div v-if="step === 'setup'" class="flex flex-col gap-4">
      <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700">
        <p class="mb-3 text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.SCAN_QR') }}
        </p>
        <div
          class="flex justify-center p-3 bg-white rounded-lg"
          v-html="qrSvg"
        />
        <p class="mt-3 text-xs text-center text-slate-500 dark:text-slate-400">
          {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.MANUAL_KEY') }}:
          <code class="px-1 py-0.5 bg-slate-200 dark:bg-slate-600 rounded text-xs font-mono break-all">
            {{ otpSecret }}
          </code>
        </p>
      </div>

      <woot-input
        v-model="otpCode"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        :styles="inputStyles"
        :label="$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.VERIFY_CODE_LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.TWO_FACTOR.VERIFY_CODE_PLACEHOLDER')"
      />

      <div class="flex gap-2">
        <FormButton
          color-scheme="primary"
          variant="solid"
          size="medium"
          :disabled="isLoading"
          @click="confirmEnable"
        >
          {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.CONFIRM_ENABLE') }}
        </FormButton>
        <FormButton
          color-scheme="secondary"
          variant="ghost"
          size="medium"
          @click="cancel"
        >
          {{ $t('PROFILE_SETTINGS.FORM.TWO_FACTOR.CANCEL') }}
        </FormButton>
      </div>
    </div>

  </div>
</template>
